import { type ApiAddress } from '@Model';
import { useStateFromProps, useTranslate } from '@synqo/frontend-component-library';
import { useCallback } from 'react';
import { offerableLocalities } from './cityMatching';
import { describeCity, type CityNotice } from './cityNotice';
import { useCityDecision, type CityDecisionState } from './useCityDecision';
import { useLocalitiesForPostalCode, type LocalityLookup } from './useLocalitiesForPostalCode';
import { useLocalityHandover, type LocalityHandover } from './useLocalityHandover';

// @ai-generated 2026-09-21T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

export interface UseLocalityAutomationArgs {
    apiAddress: ApiAddress;
    /** Persists a city the automation determined. */
    applyCity: (city: string) => void;
    /**
     * False where the city may not be written at all. Nothing is then looked up, offered or said:
     * where there is nothing to fill in there is nothing to decide either, and a contradiction the
     * user cannot act on is not worth raising.
     */
    enabled: boolean;
    /** True while the frame is in a write mode — see `useCityDecision`. */
    isEditing: boolean;
}

/** Everything the postal code and the city field need in order to act as one. */
export interface LocalityAutomation extends CityDecisionState, LocalityHandover {
    /** Reports what the postal code field shows, which is what the lookup follows. */
    onZipTyped: (zip: string) => void;

    /** The directory's answer about the postal code on screen. */
    lookup: LocalityLookup;

    /**
     * The localities as the city field's suggestion list — empty unless they are an actual choice,
     * so that a postal code with one locality never puts a dropdown in the way.
     */
    offered: string[];

    /** What is said below the city field about the answer, if anything. */
    notice: CityNotice | undefined;
}

/**
 * The postal code to city automation: the postal code field drives the lookup, the city field
 * interprets its answer. It sits above both because the postal code being typed is what is asked
 * about, while the answer is acted on at the city — down to the cursor, which it moves from the one
 * to the other when the answer holds a choice. Nothing here writes to the record on its own beyond
 * what `useCityDecision` decides.
 */
export function useLocalityAutomation({
    apiAddress,
    applyCity,
    enabled,
    isEditing,
}: UseLocalityAutomationArgs): LocalityAutomation {
    const i18n = useTranslate('');
    const { id: addressId, country } = apiAddress;
    const city = apiAddress.city ?? '';

    // The postal code on screen leads the record, which only catches up when the field is left. The
    // lookup follows the field, so the city is there before the postal code has been committed.
    // Mirrored from the record so that a discard, an undo or a change from elsewhere is picked up.
    const [typedZip, setTypedZip] = useStateFromProps(apiAddress.zip_code ?? '');
    const zip = typedZip.trim();

    const lookup = useLocalitiesForPostalCode(country, zip, enabled);
    const decision = useCityDecision({ lookup, addressId, city, applyCity, enabled, isEditing });

    const offered = offerableLocalities(lookup.localities);

    const { noteZipTyped } = decision;
    const onZipTyped = useCallback(
        (value: string) => {
            // Only the field calls this, which makes it the one place the user asks a question.
            noteZipTyped();
            setTypedZip(value);
        },
        [noteZipTyped, setTypedZip],
    );

    // A choice to make, and the postal code was the user's own doing. The cursor follows once the
    // field shows what this answer makes of it — which may take a render or two to arrive.
    const handover = useLocalityHandover(offered.length > 0 && decision.hasTypedZip, decision.cityWhenSettled);

    const notice = describeCity(
        {
            lookup,
            offered,
            city: decision.cityWhenSettled,
            isCityOurs: decision.isCityOurs,
            keptCity: decision.keptCity,
            hasEdited: decision.hasEdited,
        },
        i18n,
    );

    return { ...decision, ...handover, onZipTyped, lookup, offered, notice };
}
